import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { IDB_NAME, IDB_VERSION, UPLOAD_ROW_TTL_MS } from '@/config/constants';
import type { UploadRecord } from '@/stores/upload';

/** §3.4 — IndexedDB mirror of upload sessions (no File objects). */
export type UploadRow = {
  client_upload_id: string;
  workspace_id: string;
  upload_id?: string;
  asset_id?: string;
  version_id?: string;
  target_board_id: string;
  name: string;
  size: number;
  lastModified: number;
  detected_mime: string;
  sha256?: string;
  hash_kind?: 'full' | 'sampled';
  part_size?: number;
  part_count?: number;
  completed_parts: { n: number; etag: string }[];
  status: string;
  createdAt: number;
  is_new_version?: boolean;
};

interface UploadsDB extends DBSchema {
  uploads: { key: string; value: UploadRow; indexes: { by_ws: string } };
  handles: { key: string; value: { client_upload_id: string; handle: FileSystemFileHandle } };
}

let dbp: Promise<IDBPDatabase<UploadsDB>> | null = null;
function db(): Promise<IDBPDatabase<UploadsDB>> {
  return (dbp ??= openDB<UploadsDB>(IDB_NAME, IDB_VERSION, {
    upgrade(d) {
      const s = d.createObjectStore('uploads', { keyPath: 'client_upload_id' });
      s.createIndex('by_ws', 'workspace_id');
      d.createObjectStore('handles', { keyPath: 'client_upload_id' });
    },
  }));
}

export function rowFromRecord(r: UploadRecord): UploadRow {
  const completed_parts: { n: number; etag: string }[] = [];
  for (const [n, p] of r.parts) if (p.etag) completed_parts.push({ n, etag: p.etag });
  completed_parts.sort((a, b) => a.n - b.n);
  return {
    client_upload_id: r.client_upload_id,
    workspace_id: r.workspace_id,
    upload_id: r.upload_id,
    asset_id: r.asset_id,
    version_id: r.version_id,
    target_board_id: r.target_board_id,
    name: r.name,
    size: r.size,
    lastModified: r.lastModified,
    detected_mime: r.detected_mime,
    sha256: r.sha256,
    hash_kind: r.hash_kind,
    part_size: r.part_size,
    part_count: r.part_count,
    completed_parts,
    status: r.status,
    createdAt: r.createdAt,
    is_new_version: r.is_new_version,
  };
}

const pending = new Map<string, UploadRow>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
/** Writes are batched (≤ every 500 ms) — §3.3 step 4. */
export function persistRow(row: UploadRow): void {
  pending.set(row.client_upload_id, row);
  if (!flushTimer) flushTimer = setTimeout(() => void flush(), 500);
}
export async function flush(): Promise<void> {
  flushTimer = null;
  if (pending.size === 0) return;
  const rows = [...pending.values()];
  pending.clear();
  try {
    const d = await db();
    const tx = d.transaction('uploads', 'readwrite');
    await Promise.all([...rows.map((r) => tx.store.put(r)), tx.done]);
  } catch {
    /* storage unavailable: resume is best-effort */
  }
}
export async function deleteRow(id: string): Promise<void> {
  pending.delete(id);
  try {
    const d = await db();
    await Promise.all([d.delete('uploads', id), d.delete('handles', id)]);
  } catch {
    /* ignore */
  }
}
export async function listRows(workspaceId: string): Promise<UploadRow[]> {
  try {
    const d = await db();
    return await d.getAllFromIndex('uploads', 'by_ws', workspaceId);
  } catch {
    return [];
  }
}
export async function purgeOld(now = Date.now()): Promise<number> {
  try {
    const d = await db();
    const all = await d.getAll('uploads');
    let n = 0;
    for (const r of all) {
      if (now - r.createdAt > UPLOAD_ROW_TTL_MS || r.status === 'cancelled' || r.status === 'ready') {
        await d.delete('uploads', r.client_upload_id);
        await d.delete('handles', r.client_upload_id);
        n++;
      }
    }
    return n;
  } catch {
    return 0;
  }
}
export async function putHandle(id: string, handle: FileSystemFileHandle): Promise<void> {
  try {
    await (await db()).put('handles', { client_upload_id: id, handle });
  } catch {
    /* ignore */
  }
}
export async function getHandle(id: string): Promise<FileSystemFileHandle | undefined> {
  try {
    return (await (await db()).get('handles', id))?.handle;
  } catch {
    return undefined;
  }
}
